import type { ReactNode } from 'react';

import Heading, { HeadingContextProvider, type HeadingProps } from '@atlaskit/heading';

export type JsmPlaygroundHeadingProps = {
	children: ReactNode;
	/** Unique identifier for the rendered element. */
	id?: string;
	/** Semantic level, surfaced to assistive technology as `aria-level`. */
	level: 1 | 2 | 3 | 4 | 5 | 6;
	/** ADS type ramp step. Chosen independently of `level`, as it is in product UI. */
	size: NonNullable<HeadingProps['size']>;
};

/**
 * A heading for the playground, rendered as a `div` rather than an `h1`–`h6`.
 *
 * The playground is product UI rendered inside the marketing site, and WAC's
 * `<TypographyStyles>` sizes bare `h1`–`h6` with a descendant selector that outranks the
 * single class ADS puts on the heading itself — so a real heading tag comes out at marketing
 * sizes, where an `h2` is 2.5rem. A `div` is never matched by that selector, which leaves
 * `size` as the only thing deciding how the heading looks.
 *
 * `HeadingContextProvider` is what makes ADS emit `role="heading"` and `aria-level`. Without a
 * level in context it renders an unlabelled `div`, so the level is required here.
 */
export const JsmPlaygroundHeading = ({ children, id, level, size }: JsmPlaygroundHeadingProps) => (
	<HeadingContextProvider value={level}>
		<Heading as="div" id={id} size={size}>
			{children}
		</Heading>
	</HeadingContextProvider>
);
